
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { PiggyBank, Puzzle, TrendingDown, Heart, Calendar, Check } from "lucide-react";
import { LainaCta } from "@/components/common/LainaCta";
import { getOptimizedImageUrl } from "@/utils/imageOptimizer";

const PromotionalSection = () => {
  // Optimize paths for better performance
  const weddingImagePath = getOptimizedImageUrl("/lovable-uploads/cb302d8d-e104-44b7-9de6-77fe6f301e21.png", 400);
  const puzzleImagePath = getOptimizedImageUrl("/lovable-uploads/8bfd2c56-af00-4b88-ae7f-7d61484d4472.png", 400);

  return (
    <section className="py-12 bg-gradient-to-r from-blue-50 to-blue-100">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Promotional cards with identical structure */}
          <PromotionalCard 
            title="Unelmiesi häät"
            description="Häälaina tarjoaa joustavan rahoituksen täydellisten häidesi järjestämiseen."
            imagePath={weddingImagePath}
            imageAlt="Hääkimppu - rahoita häät häälainalla"
            ctaLink="/lainat/haalaina"
            ctaText="Tutustu häälainaan"
            ctaDataTestId="wedding-loan-cta"
            gradientColors="from-pink-600 to-pink-800"
            ctaColor="bg-pink-600 hover:bg-pink-700"
            titleColor="text-pink-700"
            checkColor="text-pink-500"
            benefits={[
              "Jopa 50 000€ ilman vakuuksia",
              "Joustavat takaisinmaksuajat",
              "Toteuta häät ilman kompromisseja",
            ]}
          />
          
          <PromotionalCard 
            title="Kevennä kuukausikulujasi"
            description="Yhdistä kaikki vanhat lainasi yhdeksi edullisemmaksi kokonaisuudeksi ja selkeytä taloutesi hallintaa."
            imagePath={puzzleImagePath}
            imageAlt="Palapelin palat - yhdistä lainasi yhdeksi kokonaisuudeksi"
            ctaLink="/lainat/yhdistelylaina"
            ctaText="Tutustu miten säästät yhdistelylainalla"
            ctaDataTestId="combine-loan-cta"
            gradientColors="from-blue-600 to-blue-800"
            ctaColor="bg-blue-600 hover:bg-blue-700"
            titleColor="text-blue-700"
            checkColor="text-green-500"
            benefits={[
              "Pienennä kokonaiskorkoa ja kuukausieriä",
              "Säästä jopa satoja euroja kuukaudessa",
              "Selkeämpi hallinta - vain yksi laina",
            ]}
            icons={[<TrendingDown />, <PiggyBank />, <Puzzle />]}
          />
        </div>
      </div>
    </section>
  );
};

// Reusable card component for promotional content
interface PromotionalCardProps {
  title: string;
  description: string;
  imagePath: string;
  imageAlt: string;
  ctaLink: string;
  ctaText: string;
  ctaDataTestId: string;
  gradientColors: string;
  ctaColor: string;
  titleColor: string;
  checkColor: string;
  benefits: string[];
  icons?: React.ReactNode[];
}

const PromotionalCard = ({
  title,
  description,
  imagePath,
  imageAlt,
  ctaLink,
  ctaText,
  ctaDataTestId,
  gradientColors,
  ctaColor,
  titleColor,
  checkColor,
  benefits,
  icons,
}: PromotionalCardProps) => {
  return (
    <Card className="overflow-hidden bg-white border-0 shadow-lg hover:shadow-xl transition-shadow h-full">
      <div className="grid grid-cols-1 md:grid-cols-5 h-full">
        {/* Content section - 3/5 width */}
        <div className="p-6 md:col-span-3 flex flex-col justify-between">
          <div>
            <h3 className={`text-2xl font-bold ${titleColor} mb-2`}>{title}</h3>
            <p className="text-gray-600 mb-4">{description}</p>
            <ul className="mb-6 space-y-2">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center">
                  {icons ? (
                    <span className={`h-5 w-5 ${checkColor} mr-2 flex-shrink-0`}>
                      {icons[index] || <Check />}
                    </span>
                  ) : (
                    <Check className={`h-5 w-5 ${checkColor} mr-2 flex-shrink-0`} />
                  )}
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          <LainaCta 
            to={ctaLink} 
            text={ctaText}
            variant="primary"
            className={`${ctaColor} w-full md:w-auto mt-auto`}
            dataTestId={ctaDataTestId}
          />
        </div>
        
        {/* Image section - 2/5 width */}
        <div className={`bg-gradient-to-br ${gradientColors} md:col-span-2 flex items-center justify-center p-6`}>
          <img 
            src={imagePath} 
            alt={imageAlt} 
            className="rounded-md shadow-lg max-h-60 object-cover"
            width="300"
            height="240"
            loading="lazy"
            fetchPriority="low"
          />
        </div>
      </div>
    </Card>
  );
};

export default PromotionalSection;
